import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { useServices } from '../hooks/useServices';
import { useProjects } from '../hooks/useProjects';
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    services,
    loading: servicesLoading
  } = useServices();
  const {
    projects,
    loading: projectsLoading
  } = useProjects();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (servicesLoading) {
    return <main className="pt-32 pb-20 min-h-screen flex items-center justify-center" data-id="element-1438">
        <div className="animate-pulse flex flex-col items-center" data-id="element-1439">
          <div className="w-12 h-12 border-4 border-[#00316B] border-t-transparent rounded-full animate-spin mb-4" data-id="element-1440" />
          <p className="text-gray-500" data-id="element-1441">Loading service details...</p>
        </div>
      </main>;
  }
  const service = services.find(s => s.slug === slug);
  if (!service) {
    return <main className="pt-32 pb-20 min-h-screen" data-id="element-1442">
        <Section background="default" data-id="element-1443">
          <div className="text-center max-w-2xl mx-auto" data-id="element-1444">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1445">
              Service Not Found
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-1446">
              The service you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/services" className="inline-flex items-center text-[#FF6600] font-medium hover:text-[#CC5200] transition-colors" data-id="element-1447">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1448" />
              Back to Services
            </Link>
          </div>
        </Section>
      </main>;
  }
  // Find projects that match this service's title
  const relatedProjects = projects.filter(p => p.category === service.title);
  return <main data-id="element-1449">
      {/* Full-bleed Hero Banner */}
      <div className="relative w-full aspect-[21/9] min-h-[320px] md:min-h-[400px] bg-[#00316B]" data-id="element-1450">
        {service.image && <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-1451" />}
        {/* Blue overlay */}
        <div className="absolute inset-0 bg-[#00316B]/50" data-id="element-1452" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-id="element-1453">
          <Link to="/services" className="inline-flex items-center text-sm font-medium text-white/70 hover:text-white transition-colors mb-6 w-fit" data-id="element-1454">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1455" />
            All Services
          </Link>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight" data-id="element-1456">
            {service.title}
          </motion.h1>
        </div>
      </div>

      {/* Description Section */}
      <Section background="default" data-id="element-1457">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="prose prose-lg text-gray-600 leading-relaxed whitespace-pre-wrap w-full max-w-none" data-id="element-1458">
          {service.description}
        </motion.div>
      </Section>

      {/* What We Offer — Specialities from Contentful */}
      {service.specialities && service.specialities.length > 0 && <Section background="white" data-id="element-1459">
          <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-12" data-id="element-1460">
            What we offer
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1461">
            {service.specialities.map((spec, index) => {
          const placeholderImages = ['https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=500&fit=crop'];
          const imgSrc = spec.image || placeholderImages[index % placeholderImages.length];
          return <motion.div key={spec.id} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100" data-id="element-1462">
                  <div className="aspect-[8/5] bg-gray-100 overflow-hidden" data-id="element-1463">
                    <img src={imgSrc} alt={spec.title} className="w-full h-full object-cover" data-id="element-1464" />
                  </div>
                  <div className="border-l-4 border-[#FF6600] p-6" data-id="element-1465">
                    <h3 className="text-lg font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-1466">
                      {spec.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-1467">
                      {spec.description}
                    </p>
                  </div>
                </motion.div>;
        })}
          </div>
        </Section>}

      {/* Related Projects Section */}
      <Section background="white" data-id="element-1468">
        <div className="mb-12" data-id="element-1469">
          <h2 className="text-3xl font-bold font-heading text-[#0C0C0C]" data-id="element-1470">
            Selected Work
          </h2>
          <p className="text-gray-600 mt-2" data-id="element-1471">
            Recent projects in {service.title.toLowerCase()}
          </p>
        </div>

        {projectsLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse" data-id="element-1472">
            {[1, 2].map(i => <div key={i} data-id="element-1473">
                <div className="aspect-[4/3] rounded-xl bg-gray-200 mb-6" data-id="element-1474" />
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" data-id="element-1475" />
                <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-1476" />
              </div>)}
          </div> : relatedProjects.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1477">
            {relatedProjects.map((project, index) => {
          const bgColor = cardColors[index % cardColors.length];
          const href = project.live_url || '#';
          return <motion.article key={project.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group cursor-pointer" data-id="element-1478">
                  <a href={href} target={project.live_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block" data-id="element-1479">
                    <div className={`aspect-[4/3] rounded-xl overflow-hidden mb-6 ${project.cover_image ? 'bg-gray-100' : bgColor} relative`} data-id="element-1480">
                      {project.cover_image ? <img src={project.cover_image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1481" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-1482">
                          <div className="w-3/4 h-3/4 bg-white/50 rounded-lg shadow-lg flex items-center justify-center" data-id="element-1483">
                            <span className="text-gray-400 text-sm" data-id="element-1484">
                              Project Preview
                            </span>
                          </div>
                        </div>}
                      <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md" data-id="element-1485">
                        <ArrowUpRight className="w-5 h-5 text-[#0C0C0C]" data-id="element-1486" />
                      </div>
                    </div>

                    <div className="mb-3" data-id="element-1487">
                      <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] group-hover:text-[#00316B] transition-colors" data-id="element-1488">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-1489">
                        {project.client_name ? `Client: ${project.client_name}` : project.category}
                      </p>
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2" data-id="element-1490">
                      {project.description}
                    </p>
                  </a>
                </motion.article>;
        })}
          </div> : <div className="bg-gray-50 rounded-2xl p-12 text-center" data-id="element-1491">
            <p className="text-gray-500" data-id="element-1492">
              No projects showcased in this category yet. Check back soon!
            </p>
          </div>}
      </Section>

      <CTASection data-id="element-1493" />
    </main>;
}